<template>
  <footer class="site-footer">
    <div class="site-footer__inner">
      <div class="site-footer__grid">
        <div class="site-footer__brand">
          <router-link
            :to="copyLink('/')"
            class="site-footer__wordmark"
            aria-label="Prio, inicio"
          >
            prio<span class="site-footer__dot" aria-hidden="true">.</span>
          </router-link>
          <p class="site-footer__tagline">
            Para quienes viven de lo que hacen. Consultas, presupuestos, proyectos y documentos en
            un solo sistema.
          </p>
          <a
            href="https://instagram.com/prio_ar"
            target="_blank"
            rel="noopener noreferrer"
            class="site-footer__social"
            aria-label="Prio en Instagram"
            data-action="footer-instagram"
          >
            <svg
              class="site-footer__social-icon"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
              />
            </svg>
          </a>
        </div>
        <nav aria-label="Producto">
          <h2 class="site-footer__heading">Producto</h2>
          <ul class="site-footer__list">
            <li>
              <router-link
                :to="copyLink('/consultas')"
                class="site-footer__link"
                data-action="footer-link"
                data-context="consultas"
                >Consultas con IA</router-link
              >
            </li>
            <li>
              <router-link
                :to="copyLink('/presupuestos')"
                class="site-footer__link"
                data-action="footer-link"
                data-context="presupuestos"
                >Presupuestos con IA</router-link
              >
            </li>
            <li>
              <router-link
                :to="copyLink('/proyectos')"
                class="site-footer__link"
                data-action="footer-link"
                data-context="proyectos"
                >Proyectos</router-link
              >
            </li>
            <li>
              <router-link
                :to="copyLink('/catalogo')"
                class="site-footer__link"
                data-action="footer-link"
                data-context="catalogo"
                >{{ isCopyV2 ? 'Catálogo' : 'Mis Servicios' }}</router-link
              >
            </li>
            <li>
              <router-link
                :to="copyLink('/documentos')"
                class="site-footer__link"
                data-action="footer-link"
                data-context="documentos"
                >Documentos</router-link
              >
            </li>
          </ul>
        </nav>
        <nav aria-label="Legal">
          <h2 class="site-footer__heading">Legal</h2>
          <ul class="site-footer__list">
            <li>
              <router-link
                :to="copyLink('/terminos')"
                class="site-footer__link"
                data-action="footer-link"
                data-context="terminos"
                >Términos y Condiciones</router-link
              >
            </li>
            <li>
              <router-link
                :to="copyLink('/privacidad')"
                class="site-footer__link"
                data-action="footer-link"
                data-context="privacidad"
                >Política de Privacidad</router-link
              >
            </li>
            <li>
              <router-link
                :to="copyLink('/cookies')"
                class="site-footer__link"
                data-action="footer-link"
                data-context="cookies"
                >Política de Cookies</router-link
              >
            </li>
            <li>
              <router-link
                :to="copyLink('/aviso-legal')"
                class="site-footer__link"
                data-action="footer-link"
                data-context="aviso-legal"
                >Aviso Legal</router-link
              >
            </li>
          </ul>
        </nav>
      </div>
      <div class="site-footer__bottom">
        <p>© {{ year }} Prio. Todos los derechos reservados.</p>
        <p>Hecho con ❤️ en Pinamar</p>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useCopyVariant } from '@/composables/useCopyVariant';

const { isCopyV2, copyLink } = useCopyVariant();

const year: number = new Date().getFullYear();
</script>

<style scoped>
.site-footer {
  border-top: 1px solid var(--line);
  background: #efe7f8;
  color: var(--muted);
}
.site-footer__inner {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: 48px 0 28px;
}
.site-footer__grid {
  display: grid;
  gap: 32px;
}
.site-footer__wordmark {
  font-family: 'Urbanist', sans-serif;
  font-size: 31px;
  font-weight: 900;
  letter-spacing: -2px;
  line-height: 1;
  color: var(--ink);
}
.site-footer__dot {
  color: var(--pink);
}
.site-footer__tagline {
  font-size: 0.85rem;
  line-height: 1.7;
  max-width: 26rem;
  margin: 14px 0 18px;
}
.site-footer__social {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid #c9b8dc;
  color: var(--violet);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}
.site-footer__social:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(101, 65, 189, 0.22);
}
.site-footer__social-icon {
  width: 20px;
  height: 20px;
}
.site-footer__heading {
  font-family: 'Urbanist', sans-serif;
  font-size: 0.8rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--ink);
  margin: 0 0 14px;
}
.site-footer__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.site-footer__link {
  font-size: 0.88rem;
  color: var(--muted);
}
.site-footer__link:hover {
  color: var(--violet);
}
.site-footer__bottom {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 36px;
  padding-top: 24px;
  border-top: 1px solid var(--line);
  font-size: 0.82rem;
}
a:focus-visible {
  outline: 3px solid var(--violet);
  outline-offset: 4px;
}

@media (min-width: 768px) {
  .site-footer__grid {
    grid-template-columns: 1.4fr 1fr 1fr;
  }
  .site-footer__bottom {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
